import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Users, Plus, Edit, Trash2, Shield, AlertTriangle, UserX, FileSpreadsheet, MoreVertical, Euro, Tags, KeyRound } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ImportarUsuariosDialog } from '@/components/usuarios/ImportarUsuariosDialog';
import { TarifaTecnicoDialog } from '@/components/usuarios/TarifaTecnicoDialog';
import { CategoriasTarifaDialog } from '@/components/usuarios/CategoriasTarifaDialog';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Perfil {
  id: string;
  user_id: string;
  nombre: string;
  email: string;
  rol: 'admin' | 'tecnico' | 'gestor';
  estado: 'activo' | 'inactivo';
  created_at: string;
  updated_at: string;
}

interface TarifaInfo {
  coste_hora: number;
  coste_hora_extra: number | null;
  importe_furgoneta: number | null;
  categoria_nombre: string | null;
}

interface ActividadUsuario {
  partes_trabajo: number;
  ordenes_asignadas: number;
  ordenes_creadas: number;
  checklists: number;
  jornadas: number;
  anomalias: number;
  total: number;
}

const GestionUsuarios = () => {
  const [perfiles, setPerfiles] = useState<Perfil[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPerfil, setEditingPerfil] = useState<Perfil | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [actividadUsuarios, setActividadUsuarios] = useState<Record<string, ActividadUsuario>>({});
  const [loadingActividad, setLoadingActividad] = useState<Record<string, boolean>>({});
  const [tarifaUsuario, setTarifaUsuario] = useState<Perfil | null>(null);
  const [categoriasOpen, setCategoriasOpen] = useState(false);
  const [tarifasUsuarios, setTarifasUsuarios] = useState<Record<string, TarifaInfo | null>>({});
  const [resetPwdUser, setResetPwdUser] = useState<Perfil | null>(null);
  const [resetPwdValue, setResetPwdValue] = useState('');
  const [resetPwdConfirm, setResetPwdConfirm] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    rol: 'tecnico' as 'admin' | 'tecnico' | 'gestor',
    estado: 'activo' as 'activo' | 'inactivo'
  });
  const { perfil, signOut } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (perfil && perfil.rol !== 'admin') {
      navigate('/clientes');
      return;
    }
    fetchPerfiles();
  }, [perfil, navigate]);

  const fetchPerfiles = async () => {
    try {
      const { data, error } = await supabase
        .from('perfiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching perfiles:', error);
        toast({
          title: "Error",
          description: "Error al cargar los usuarios.",
          variant: "destructive",
        });
      } else {
        const list = (data || []) as Perfil[];
        setPerfiles(list);
        fetchTarifasVigentes(list);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTarifasVigentes = async (lista: Perfil[]) => {
    const ids = lista
      .filter(p => p.rol === 'tecnico' || p.rol === 'admin')
      .map(p => p.user_id);
    if (ids.length === 0) return;
    try {
      const { data, error } = await supabase
        .from('tecnico_tarifas')
        .select('tecnico_id, coste_hora, coste_hora_extra, importe_furgoneta, vigente_desde, vigente_hasta, tarifas_categoria(nombre)')
        .in('tecnico_id', ids)
        .is('vigente_hasta', null);
      if (error) {
        console.error('Error fetching tarifas:', error);
        return;
      }
      const map: Record<string, TarifaInfo | null> = {};
      (data || []).forEach((r: any) => {
        map[r.tecnico_id] = {
          coste_hora: Number(r.coste_hora),
          coste_hora_extra: r.coste_hora_extra != null ? Number(r.coste_hora_extra) : null,
          importe_furgoneta: r.importe_furgoneta != null ? Number(r.importe_furgoneta) : null,
          categoria_nombre: r.tarifas_categoria?.nombre ?? null,
        };
      });
      setTarifasUsuarios(map);
    } catch (e) {
      console.error('Error fetching tarifas:', e);
    }
  };

  const verificarActividadUsuario = async (userId: string): Promise<ActividadUsuario> => {
    setLoadingActividad(prev => ({ ...prev, [userId]: true }));
    
    try {
      const [partes, ordenesAsignadas, ordenesCreadas, checklists, jornadas, anomalias] = await Promise.all([
        supabase.from('partes_trabajo').select('id', { count: 'exact', head: true }).eq('tecnico_id', userId),
        supabase.from('ordenes_trabajo').select('id', { count: 'exact', head: true }).eq('tecnico_asignado_id', userId),
        supabase.from('ordenes_trabajo').select('id', { count: 'exact', head: true }).eq('creada_por', userId),
        supabase.from('checklists_seguridad').select('id', { count: 'exact', head: true }).eq('tecnico_id', userId),
        supabase.from('obra_jornadas').select('id', { count: 'exact', head: true }).eq('tecnico_id', userId),
        supabase.from('anomalias_elementos').select('id', { count: 'exact', head: true }).eq('tecnico_id', userId),
      ]);

      const actividad: ActividadUsuario = {
        partes_trabajo: partes.count || 0,
        ordenes_asignadas: ordenesAsignadas.count || 0,
        ordenes_creadas: ordenesCreadas.count || 0,
        checklists: checklists.count || 0,
        jornadas: jornadas.count || 0,
        anomalias: anomalias.count || 0,
        total: (partes.count || 0) + (ordenesAsignadas.count || 0) + (ordenesCreadas.count || 0) + 
               (checklists.count || 0) + (jornadas.count || 0) + (anomalias.count || 0)
      };

      setActividadUsuarios(prev => ({ ...prev, [userId]: actividad }));
      return actividad;
    } finally {
      setLoadingActividad(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleCreateUser = async () => {
    if (!formData.nombre || !formData.email || !formData.password) {
      toast({
        title: "Error",
        description: "Todos los campos son obligatorios.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);

      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          email: formData.email,
          password: formData.password,
          nombre: formData.nombre,
          rol: formData.rol,
        },
      });

      const errorMsg = (data as any)?.error || (error as any)?.context?.error || error?.message;

      if (errorMsg) {
        toast({
          title: "Error",
          description: errorMsg,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Usuario creado",
        description: "Usuario creado correctamente",
      });

      setDialogOpen(false);
      setFormData({
        nombre: '',
        email: '',
        password: '',
        rol: 'tecnico' as 'admin' | 'tecnico' | 'gestor',
        estado: 'activo' as 'activo' | 'inactivo'
      });
      
      setTimeout(() => {
        fetchPerfiles();
      }, 1000);

    } catch (error) {
      console.error('Error creating user:', error);
      toast({
        title: "Error",
        description: "Error al crear el usuario.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditUser = async () => {
    if (!editingPerfil) return;

    try {
      setIsLoading(true);

      const { error } = await supabase
        .from('perfiles')
        .update({
          nombre: formData.nombre,
          rol: formData.rol,
          estado: formData.estado
        })
        .eq('id', editingPerfil.id);

      if (error) {
        toast({
          title: "Error",
          description: "Error al actualizar el usuario.",
          variant: "destructive",
        });
      } else {
        setPerfiles(prevPerfiles => 
          prevPerfiles.map(p => 
            p.id === editingPerfil.id 
              ? { ...p, nombre: formData.nombre, rol: formData.rol, estado: formData.estado }
              : p
          )
        );
        
        toast({
          title: "Usuario actualizado",
          description: "Los cambios han sido guardados.",
        });
        setDialogOpen(false);
        setEditingPerfil(null);
        fetchPerfiles();
      }
    } catch (error) {
      console.error('Error updating user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDesactivarUsuario = async (perfilToDeactivate: Perfil) => {
    try {
      setIsLoading(true);

      const { error } = await supabase
        .from('perfiles')
        .update({ estado: 'inactivo' })
        .eq('id', perfilToDeactivate.id);

      if (error) {
        toast({
          title: "Error",
          description: "Error al desactivar el usuario.",
          variant: "destructive",
        });
      } else {
        setPerfiles(prevPerfiles => 
          prevPerfiles.map(p => 
            p.id === perfilToDeactivate.id 
              ? { ...p, estado: 'inactivo' }
              : p
          )
        );
        
        toast({
          title: "Usuario desactivado",
          description: `${perfilToDeactivate.nombre} ha sido desactivado. No podrá iniciar sesión pero sus registros históricos se conservan.`,
        });
      }
    } catch (error) {
      console.error('Error deactivating user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPwdUser) return;
    if (resetPwdValue.length < 6) {
      toast({ title: "Error", description: "La contraseña debe tener al menos 6 caracteres.", variant: "destructive" });
      return;
    }
    if (resetPwdValue !== resetPwdConfirm) {
      toast({ title: "Error", description: "Las contraseñas no coinciden.", variant: "destructive" });
      return;
    }
    try {
      setIsResetting(true);
      const { data, error } = await supabase.functions.invoke('reset-user-password', {
        body: { user_id: resetPwdUser.user_id, password: resetPwdValue },
      });
      const errorMsg = (data as any)?.error || (error as any)?.context?.error || error?.message;
      if (errorMsg) {
        toast({ title: "Error", description: errorMsg, variant: "destructive" });
        return;
      }
      toast({ title: "Contraseña actualizada", description: `Nueva contraseña asignada a ${resetPwdUser.nombre}.` });
      setResetPwdUser(null);
      setResetPwdValue('');
      setResetPwdConfirm('');
    } catch (e: any) {
      toast({ title: "Error", description: e?.message || "Error al resetear la contraseña.", variant: "destructive" });
    } finally {
      setIsResetting(false);
    }
  };

  const handleDeleteUser = async (perfilToDelete: Perfil) => {
    try {
      setIsLoading(true);

      const response = await fetch('https://ppmfxnwbanrqgsmydzjo.supabase.co/functions/v1/delete-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer qseguridad-delete-user-7kf82nx91p'
        },
        body: JSON.stringify({
          user_id: perfilToDelete.user_id
        })
      });

      const result = await response.json();

      if (!response.ok) {
        if (result.error === 'TIENE_ACTIVIDAD') {
          toast({
            title: "No se puede eliminar",
            description: "El usuario tiene actividad histórica. Desactívelo en su lugar.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error",
            description: result.error || "Error al eliminar el usuario.",
            variant: "destructive",
          });
        }
        return;
      }

      toast({
        title: "Usuario eliminado",
        description: "El usuario ha sido eliminado permanentemente.",
      });
      
      setPerfiles(prevPerfiles => prevPerfiles.filter(p => p.id !== perfilToDelete.id));
      
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: "Error",
        description: "Error al eliminar el usuario.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingPerfil(null);
    setFormData({
      nombre: '',
      email: '',
      password: '',
      rol: 'tecnico' as 'admin' | 'tecnico' | 'gestor',
      estado: 'activo' as 'activo' | 'inactivo'
    });
    setDialogOpen(true);
  };

  const openEditDialog = (perfilItem: Perfil) => {
    setEditingPerfil(perfilItem);
    setFormData({
      nombre: perfilItem.nombre,
      email: perfilItem.email,
      password: '',
      rol: perfilItem.rol,
      estado: perfilItem.estado
    });
    setDialogOpen(true);
  };

  const getRoleBadgeColor = (rol: string) => {
    switch (rol) {
      case 'admin': return 'destructive';
      case 'gestor': return 'default';
      default: return 'secondary';
    }
  };

  const getRoleLabel = (rol: string) => {
    switch (rol) {
      case 'admin': return 'Administrador';
      case 'gestor': return 'Gestor';
      case 'tecnico': return 'Técnico';
      default: return rol;
    }
  };

  const getEstadoBadgeColor = (estado: string) => {
    return estado === 'activo' ? 'default' : 'outline';
  };

  if (isLoading && perfiles.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2">Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 p-4 md:p-6">
      <div className="max-w-6xl mx-auto mobile-spacing">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold mobile-text">Gestión de Usuarios</h1>
              <p className="text-muted-foreground text-sm md:text-base">Administra usuarios del sistema</p>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            <span className="font-medium">{perfiles.length} usuarios registrados</span>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCategoriasOpen(true)} className="mobile-button">
              <Tags className="h-4 w-4 mr-2" />
              Categorías de tarifa
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="mobile-button">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover z-50">
                <DropdownMenuItem onClick={() => setImportDialogOpen(true)}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Importar desde Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreateDialog} className="mobile-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo Usuario
                </Button>
              </DialogTrigger>
            <DialogContent className="mx-4 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingPerfil ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
                </DialogTitle>
                <DialogDescription>
                  {editingPerfil 
                    ? 'Modifica los datos del usuario seleccionado.'
                    : 'Completa los datos para crear un nuevo usuario.'
                  }
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="nombre">Nombre</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    placeholder="Nombre completo"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="usuario@email.com"
                    disabled={!!editingPerfil}
                  />
                </div>
                {!editingPerfil && (
                  <div>
                    <Label htmlFor="password">Contraseña</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="••••••••"
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="rol">Rol</Label>
                  <Select value={formData.rol} onValueChange={(value: 'admin' | 'tecnico' | 'gestor') => setFormData({...formData, rol: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tecnico">Técnico (trabajo de campo)</SelectItem>
                      <SelectItem value="gestor">Gestor (oficina)</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editingPerfil && (
                  <div>
                    <Label htmlFor="estado">Estado</Label>
                    <Select value={formData.estado} onValueChange={(value: 'activo' | 'inactivo') => setFormData({...formData, estado: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="activo">Activo</SelectItem>
                        <SelectItem value="inactivo">Inactivo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex gap-2 pt-4">
                  <Button 
                    onClick={editingPerfil ? handleEditUser : handleCreateUser}
                    disabled={isLoading}
                    className="flex-1"
                  >
                    {editingPerfil ? 'Guardar Cambios' : 'Crear Usuario'}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setDialogOpen(false)}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <ImportarUsuariosDialog
          open={importDialogOpen}
          onOpenChange={setImportDialogOpen}
          onSuccess={() => fetchPerfiles()}
        />

        <div className="grid gap-4">
          {perfiles.map((perfilItem) => (
            <Card key={perfilItem.id} className={perfilItem.estado === 'inactivo' ? 'opacity-60' : ''}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold">{perfilItem.nombre}</h3>
                      <Badge variant={getRoleBadgeColor(perfilItem.rol)}>
                        {perfilItem.rol}
                      </Badge>
                      <Badge variant={getEstadoBadgeColor(perfilItem.estado)}>
                        {perfilItem.estado}
                      </Badge>
                    </div>
                    <p className="text-muted-foreground">{perfilItem.email}</p>
                    {(perfilItem.rol === 'tecnico' || perfilItem.rol === 'admin') && (
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        {tarifasUsuarios[perfilItem.user_id] ? (
                          <>
                            {tarifasUsuarios[perfilItem.user_id]!.categoria_nombre ? (
                              <Badge variant="secondary" className="gap-1">
                                <Tags className="h-3 w-3" />
                                {tarifasUsuarios[perfilItem.user_id]!.categoria_nombre}
                              </Badge>
                            ) : (
                              <Badge variant="outline">Tarifa manual</Badge>
                            )}
                            <Badge variant="outline" className="gap-1">
                              <Euro className="h-3 w-3" />
                              {tarifasUsuarios[perfilItem.user_id]!.coste_hora.toFixed(2)} €/h
                            </Badge>
                            {tarifasUsuarios[perfilItem.user_id]!.coste_hora_extra != null && (
                              <Badge variant="outline">
                                Extra: {tarifasUsuarios[perfilItem.user_id]!.coste_hora_extra!.toFixed(2)} €/h
                              </Badge>
                            )}
                            {tarifasUsuarios[perfilItem.user_id]!.importe_furgoneta != null &&
                              tarifasUsuarios[perfilItem.user_id]!.importe_furgoneta! > 0 && (
                              <Badge variant="outline">
                                Furgoneta: {tarifasUsuarios[perfilItem.user_id]!.importe_furgoneta!.toFixed(2)} €
                              </Badge>
                            )}
                          </>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">
                            Sin tarifa asignada
                          </Badge>
                        )}
                      </div>
                    )}
                    <p className="text-sm text-muted-foreground mt-2">
                      Creado: {new Date(perfilItem.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {(perfilItem.rol === 'tecnico' || perfilItem.rol === 'admin') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setTarifaUsuario(perfilItem)}
                        title="Tarifa de mano de obra"
                      >
                        <Euro className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setResetPwdUser(perfilItem);
                        setResetPwdValue('');
                        setResetPwdConfirm('');
                      }}
                      title="Resetear contraseña"
                    >
                      <KeyRound className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openEditDialog(perfilItem)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <DeleteUserButton 
                      perfilItem={perfilItem}
                      actividadUsuarios={actividadUsuarios}
                      loadingActividad={loadingActividad}
                      verificarActividadUsuario={verificarActividadUsuario}
                      handleDeleteUser={handleDeleteUser}
                      handleDesactivarUsuario={handleDesactivarUsuario}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {perfiles.length === 0 && !isLoading && (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium">No hay usuarios registrados</p>
              <p className="text-muted-foreground mb-4">
                Crea el primer usuario para comenzar
              </p>
              <Button onClick={openCreateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Crear Usuario
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {tarifaUsuario && (
        <TarifaTecnicoDialog
          open={!!tarifaUsuario}
          onOpenChange={(o) => {
            if (!o) {
              setTarifaUsuario(null);
              fetchTarifasVigentes(perfiles);
            }
          }}
          tecnicoId={tarifaUsuario.user_id}
          tecnicoNombre={tarifaUsuario.nombre}
        />
      )}

      <CategoriasTarifaDialog open={categoriasOpen} onOpenChange={setCategoriasOpen} />

      <Dialog open={!!resetPwdUser} onOpenChange={(o) => { if (!o) { setResetPwdUser(null); setResetPwdValue(''); setResetPwdConfirm(''); } }}>
        <DialogContent className="mx-4">
          <DialogHeader>
            <DialogTitle>Resetear contraseña</DialogTitle>
            <DialogDescription>
              Asigna una nueva contraseña a {resetPwdUser?.nombre}. El usuario podrá iniciar sesión inmediatamente con la nueva clave.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="reset-pwd">Nueva contraseña</Label>
              <Input
                id="reset-pwd"
                type="password"
                value={resetPwdValue}
                onChange={(e) => setResetPwdValue(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                autoComplete="new-password"
              />
            </div>
            <div>
              <Label htmlFor="reset-pwd-confirm">Confirmar contraseña</Label>
              <Input
                id="reset-pwd-confirm"
                type="password"
                value={resetPwdConfirm}
                onChange={(e) => setResetPwdConfirm(e.target.value)}
                placeholder="Repite la contraseña"
                autoComplete="new-password"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setResetPwdUser(null)} disabled={isResetting}>Cancelar</Button>
              <Button onClick={handleResetPassword} disabled={isResetting}>
                {isResetting ? 'Guardando...' : 'Asignar contraseña'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Componente separado para el botón de eliminar con lógica condicional
interface DeleteUserButtonProps {
  perfilItem: Perfil;
  actividadUsuarios: Record<string, ActividadUsuario>;
  loadingActividad: Record<string, boolean>;
  verificarActividadUsuario: (userId: string) => Promise<ActividadUsuario>;
  handleDeleteUser: (perfil: Perfil) => Promise<void>;
  handleDesactivarUsuario: (perfil: Perfil) => Promise<void>;
}

const DeleteUserButton: React.FC<DeleteUserButtonProps> = ({
  perfilItem,
  actividadUsuarios,
  loadingActividad,
  verificarActividadUsuario,
  handleDeleteUser,
  handleDesactivarUsuario,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const actividad = actividadUsuarios[perfilItem.user_id];
  const isLoading = loadingActividad[perfilItem.user_id];

  const handleOpenChange = async (open: boolean) => {
    if (open && !actividad) {
      await verificarActividadUsuario(perfilItem.user_id);
    }
    setDialogOpen(open);
  };

  const tieneActividad = actividad && actividad.total > 0;

  return (
    <AlertDialog open={dialogOpen} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Trash2 className="h-4 w-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-3">Verificando actividad...</span>
          </div>
        ) : tieneActividad ? (
          // Usuario CON actividad - No se puede eliminar
          <>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-5 w-5" />
                No se puede eliminar este usuario
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>
                    <span className="font-semibold">"{perfilItem.nombre}"</span> tiene actividad histórica registrada 
                    y sus datos deben conservarse para mantener la integridad del sistema.
                  </p>
                  <div className="bg-muted p-3 rounded-lg text-sm">
                    <p className="font-medium mb-2">Registros asociados:</p>
                    <ul className="space-y-1 text-muted-foreground">
                      {actividad.partes_trabajo > 0 && (
                        <li>• Partes de trabajo: {actividad.partes_trabajo}</li>
                      )}
                      {(actividad.ordenes_asignadas > 0 || actividad.ordenes_creadas > 0) && (
                        <li>• Órdenes de trabajo: {actividad.ordenes_asignadas + actividad.ordenes_creadas}</li>
                      )}
                      {actividad.checklists > 0 && (
                        <li>• Checklists: {actividad.checklists}</li>
                      )}
                      {actividad.jornadas > 0 && (
                        <li>• Jornadas de obra: {actividad.jornadas}</li>
                      )}
                      {actividad.anomalias > 0 && (
                        <li>• Anomalías registradas: {actividad.anomalias}</li>
                      )}
                    </ul>
                  </div>
                  <p className="text-sm">
                    <span className="font-medium">💡 Recomendación:</span> Puedes <strong>desactivar</strong> el usuario 
                    para impedir su acceso sin perder el histórico.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cerrar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => {
                  handleDesactivarUsuario(perfilItem);
                  setDialogOpen(false);
                }}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <UserX className="h-4 w-4 mr-2" />
                Desactivar usuario
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        ) : (
          // Usuario SIN actividad - Se puede eliminar
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>
                    El usuario <span className="font-semibold">"{perfilItem.nombre}"</span> no tiene 
                    actividad registrada y puede ser eliminado de forma segura.
                  </p>
                  <p className="text-destructive font-medium">
                    Esta acción no se puede deshacer.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => {
                  handleDeleteUser(perfilItem);
                  setDialogOpen(false);
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Eliminar permanentemente
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default GestionUsuarios;
